/**
 * Stage 6 — Activation Engine
 *
 * Premium "Welcome to Kindra" surface served immediately after onboarding.
 * Replaces the old `/activation` redirect-to-chat flow that was producing
 * 2.5% first-chat / 0% first-upload activation.
 *
 * Four instant-start paths — each must deliver value in <60 seconds:
 *   1. Upload a report
 *   2. Ask a health question
 *   3. Add one condition
 *   4. See what Kindra can do
 *
 * State machine: 'choose' → one of {'upload', 'chat', 'condition', 'demo'} → 'success'
 * All flows are inline — no navigation away from the welcome surface until the
 * user explicitly clicks the success-state CTA. This is critical for activation
 * because every navigation is a chance to drop out.
 *
 * Tracking emitted (Stage 6 events):
 *   • activation_started          — user picked a path (props.path = ...)
 *   • first_value                 — path delivered something tangible
 *   • welcome_path_completed      — user pressed "Open my home"
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios, { API } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { track as trackEvent } from '../services/activation';
import {
  Sparkle, ArrowRight, ArrowLeft, CheckCircle, Spinner,
  UploadSimple, ChatCircle, Heartbeat, Eye, Paperclip,
  PaperPlaneRight, BookBookmark, ShieldCheck,
} from '@phosphor-icons/react';
import UploadDropzone from '../components/documents/UploadDropzone';
import UploadProgressTile from '../components/documents/UploadProgressTile';
import SourceTrustStrip from '../components/chat/SourceTrustStrip';

const PATHS = [
  {
    id: 'upload',
    icon: UploadSimple,
    accent: '#2FB3A0',
    title: 'Upload a report',
    sub: 'Bloods, scans, letters — Kindra reads them in plain English.',
    cta: 'Add a report',
    time: '~30s',
  },
  {
    id: 'chat',
    icon: ChatCircle,
    accent: '#0E1C2E',
    title: 'Ask a health question',
    sub: 'About a symptom, a medication, anything on your mind right now.',
    cta: 'Ask Kindra',
    time: '~20s',
  },
  {
    id: 'condition',
    icon: Heartbeat,
    accent: '#235347',
    title: 'Add one condition',
    sub: 'Anchor your record so every chat and report is personal.',
    cta: 'Add a condition',
    time: '~20s',
  },
  {
    id: 'demo',
    icon: Eye,
    accent: '#7B61FF',
    title: 'See what Kindra can do',
    sub: 'A 60-second walkthrough — no upload, no commitment.',
    cta: 'Show me',
    time: '~60s',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Stage 8 — experiment-driven copy variants
// ─────────────────────────────────────────────────────────────────────────────
const PAUSE_COPY_VARIANTS = {
  default:      'Most people start here',
  social_proof: '84% start here',
  warm:         'A good place to start',
};

// Apply path_order experiment override on top of the server-recommended order.
function applyPathOrderVariant(orderedIds, variant) {
  if (!Array.isArray(orderedIds)) return orderedIds;
  const next = orderedIds.filter(Boolean);
  const pin = (id) => {
    if (!next.includes(id)) return next;
    const i = next.indexOf(id);
    if (i === -1) return next;
    next.splice(i, 1);
    next.unshift(id);
    return next;
  };
  if (variant === 'upload_first') return pin('upload');
  if (variant === 'chat_first')   return pin('chat');
  return next; // 'control' — leave as-is
}

// Apply demo_position experiment — default = leave at end; top_right = put
// demo into slot[1] (second card) so it sits top-right on the 2-col grid.
function applyDemoPositionVariant(orderedIds, variant) {
  if (variant !== 'top_right') return orderedIds;
  const arr = [...orderedIds];
  const idx = arr.indexOf('demo');
  if (idx === -1 || idx === 1) return arr;
  arr.splice(idx, 1);
  arr.splice(1, 0, 'demo');
  return arr;
}

// ─────────────────────────────────────────────────────────────────────────────
// Page shell
// ─────────────────────────────────────────────────────────────────────────────
const WelcomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [phase, setPhase] = useState('choose');
  const [chosenPath, setChosenPath] = useState(null);

  // Stage 7 — adaptive ordering + recommendation from the server.
  const [pathOrder, setPathOrder] = useState(PATHS.map((p) => p.id));
  const [recommended, setRecommended] = useState(null);
  const [recommendReason, setRecommendReason] = useState(null);
  // Stage 8 — variant assignments (server-deterministic per user_id).
  const [variants, setVariants] = useState({});
  const variantsRef = useRef({}); // for use inside trackEvent (avoids stale closure)
  const exposureFiredRef = useRef(false);
  // 8-second pause hint — set true once a single inactivity timer expires.
  const [showPauseHint, setShowPauseHint] = useState(false);
  const pauseFiredRef = useRef(false);

  const firstName = user?.name?.split(' ')[0] || 'there';

  // Fetch personalized order + experiments on mount.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/welcome/recommendations`);
        if (cancel) return;
        if (Array.isArray(data?.order) && data.order.length === 4) {
          setPathOrder(data.order);
        }
        if (data?.recommended) setRecommended(data.recommended);
        if (data?.reason)      setRecommendReason(data.reason);
        if (data?.experiments && typeof data.experiments === 'object') {
          setVariants(data.experiments);
          variantsRef.current = data.experiments;
        }
      } catch { /* graceful — keep defaults */ }
    })();
    return () => { cancel = true; };
  }, []);

  // Track welcome view + experiment exposures (once per page mount).
  useEffect(() => {
    trackEvent('activation_welcome_chat_opened', { surface: 'stage6_welcome' });
  }, []);

  // Fire one `experiment_exposure` event per assigned variant. Keeps the
  // analytics surface clean — we never re-fire on re-render.
  useEffect(() => {
    if (exposureFiredRef.current) return;
    if (!variants || Object.keys(variants).length === 0) return;
    exposureFiredRef.current = true;
    Object.entries(variants).forEach(([experiment, variant]) => {
      trackEvent('experiment_exposure', { experiment, variant, surface: 'welcome' });
    });
  }, [variants]);

  // Stage 7+8 — 8-second inactivity timer. Suppressed when the pause_hint
  // experiment assigns the user to "hidden".
  useEffect(() => {
    if (phase !== 'choose') return undefined;
    if (showPauseHint) return undefined;
    if (variants?.pause_hint === 'hidden') return undefined;
    const t = setTimeout(() => {
      setShowPauseHint(true);
      if (!pauseFiredRef.current) {
        pauseFiredRef.current = true;
        trackEvent('welcome_paused', {
          recommended,
          variants: variantsRef.current,
        });
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [phase, showPauseHint, recommended, variants]);

  const pickPath = (id) => {
    setChosenPath(id);
    setPhase(id);
    // Tag the pick with all active variants so post-hoc analysis can slice.
    trackEvent('activation_started', {
      path: id,
      recommended_path: recommended,
      variants: variantsRef.current,
    });
  };

  const goBack = () => {
    setPhase('choose');
    setChosenPath(null);
  };

  const finishToHome = () => {
    trackEvent('welcome_path_completed', {
      path: chosenPath,
      variants: variantsRef.current,
    });
    navigate('/home');
  };

  // Build the ordered path list, then apply the path_order + demo_position
  // experiment overrides on top of the server's Stage 7 order.
  let orderedIds = pathOrder.slice();
  // Append any paths the server missed at the end.
  PATHS.forEach((p) => { if (!orderedIds.includes(p.id)) orderedIds.push(p.id); });
  orderedIds = applyPathOrderVariant(orderedIds, variants?.path_order);
  orderedIds = applyDemoPositionVariant(orderedIds, variants?.demo_position);
  const orderedPaths = orderedIds.map((id) => PATHS.find((p) => p.id === id)).filter(Boolean);

  // Effective recommendation is the first item in the (post-experiment) order
  // when the path_order experiment forces a top — otherwise honour the server.
  const effectiveRecommended = (variants?.path_order && variants.path_order !== 'control')
    ? orderedIds[0]
    : recommended;

  return (
    <div className="min-h-screen bg-[#FAFAFA] safe-top safe-bottom" data-testid="welcome-page">
      <div className="max-w-xl lg:max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <AnimatePresence mode="wait">
          {phase === 'choose' && (
            <ChoosePhase
              key="choose"
              firstName={firstName}
              paths={orderedPaths}
              recommended={effectiveRecommended}
              recommendReason={recommendReason}
              showPauseHint={showPauseHint}
              variants={variants}
              onPick={pickPath}
              onSkip={() => navigate('/home')}
            />
          )}
          {phase === 'upload' && (
            <UploadPhase key="upload" onBack={goBack} onSuccess={finishToHome} />
          )}
          {phase === 'chat' && (
            <ChatPhase key="chat" onBack={goBack} onSuccess={finishToHome} />
          )}
          {phase === 'condition' && (
            <ConditionPhase key="condition" onBack={goBack} onSuccess={finishToHome} />
          )}
          {phase === 'demo' && (
            <DemoPhase key="demo" onBack={goBack} onSuccess={finishToHome} />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase: choose
// ─────────────────────────────────────────────────────────────────────────────
const ChoosePhase = ({ firstName, paths, recommended, recommendReason, showPauseHint, variants, onPick, onSkip }) => {
  // Stage 8 — copy + UI variants ───────────────────────────────────
  const showTimeEstimates = (variants?.time_estimates ?? 'shown') !== 'hidden';
  const pauseCopyVariant = variants?.pause_copy ?? 'default';
  const pauseCopy = PAUSE_COPY_VARIANTS[pauseCopyVariant] || PAUSE_COPY_VARIANTS.default;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.4 }}
      data-testid="welcome-choose"
    >
      {/* Header */}
      <header className="mb-6 sm:mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={12} weight="fill" className="text-[#2FB3A0]" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Welcome to Kindra
          </span>
        </div>
        <h1
          className="font-serif text-[28px] sm:text-[34px] leading-[1.05] tracking-[-0.01em] text-[#0E1C2E]"
          data-testid="welcome-title"
        >
          Hi <em className="italic">{firstName}</em>.
          <br />
          Where would you like to start?
        </h1>
        <p className="mt-3 text-[14px] text-slate-500 leading-relaxed max-w-md">
          Pick one. You'll see something useful in under a minute, then you can keep exploring.
        </p>
      </header>

      {/* 4 path cards — order is server-driven (Stage 7) + experiment-overridden (Stage 8) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="welcome-paths">
        {paths.map((p, i) => {
          const isRecommended = recommended === p.id;
          const showHint = showPauseHint && isRecommended;
          return (
            <motion.button
              key={p.id}
              type="button"
              onClick={() => onPick(p.id)}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.06 * i }}
              whileHover={{ y: -2 }}
              className={`relative bg-white rounded-2xl p-5 text-left transition-colors flex flex-col gap-3 ${
                showHint
                  ? 'border-2 border-[#2FB3A0] shadow-[0_0_0_4px_rgba(47,179,160,0.08)]'
                  : 'border border-[#ECE8E1] hover:border-[#CFC8BB] hover:bg-[#FBFAF6]'
              }`}
              data-testid={`welcome-path-${p.id}`}
              data-variant-pause-copy={pauseCopyVariant}
            >
              {/* Pause hint badge — only when active variant is "shown" + recommended */}
              {showHint && (
                <motion.div
                  initial={{ opacity: 0, y: -6, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className="absolute -top-2.5 left-3 right-3 sm:left-auto sm:right-auto sm:max-w-[260px] z-10"
                  data-testid="welcome-pause-hint"
                >
                  <div className="inline-flex items-center gap-1.5 bg-[#0E1C2E] text-white text-[10px] font-semibold uppercase tracking-[0.16em] rounded-full px-3 py-1 shadow-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2FB3A0] animate-pulse" />
                    {pauseCopy}
                  </div>
                </motion.div>
              )}

              <div className="flex items-start justify-between">
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center text-white"
                  style={{ backgroundColor: p.accent }}
                >
                  <p.icon size={20} weight="fill" />
                </div>
                {showTimeEstimates && (
                  <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400" data-testid="welcome-time-pill">
                    {p.time}
                  </span>
                )}
              </div>
              <div>
                <h3
                  className="font-serif text-[19px] leading-tight text-[#0E1C2E]"
                  style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
                >
                  {p.title}
                </h3>
                <p className="text-[12.5px] text-slate-500 leading-relaxed mt-1">{p.sub}</p>
              </div>
              <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0E1C2E] mt-1">
                {p.cta} <ArrowRight size={12} weight="bold" />
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Pause hint — quiet inline reason copy under the cards */}
      {showPauseHint && recommendReason && (
        <motion.p
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="mt-5 text-center text-[12.5px] text-slate-500 italic"
          data-testid="welcome-pause-reason"
        >
          {recommendReason}
        </motion.p>
      )}

      {/* Quiet skip */}
      <div className="mt-8 text-center">
        <button
          type="button"
          onClick={onSkip}
          className="text-[12.5px] text-slate-400 hover:text-slate-600 transition-colors"
          data-testid="welcome-skip"
        >
          I'll explore on my own →
        </button>
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase header (back button + eyebrow) — shared by all sub-phases
// ─────────────────────────────────────────────────────────────────────────────
const PhaseHeader = ({ onBack, eyebrow, title }) => (
  <header className="mb-5 sm:mb-6">
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-[#0E1C2E] mb-4 transition-colors"
      data-testid="welcome-back"
    >
      <ArrowLeft size={12} weight="bold" /> Back
    </button>
    <div className="flex items-center gap-2 mb-2">
      <Sparkle size={11} weight="fill" className="text-[#2FB3A0]" />
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
        {eyebrow}
      </span>
    </div>
    <h2
      className="font-serif text-[24px] sm:text-[28px] leading-tight tracking-[-0.005em] text-[#0E1C2E]"
    >
      {title}
    </h2>
  </header>
);

// ─────────────────────────────────────────────────────────────────────────────
// Phase: UPLOAD
// ─────────────────────────────────────────────────────────────────────────────
const UploadPhase = ({ onBack, onSuccess }) => {
  const navigate = useNavigate();
  const [tile, setTile] = useState(null);     // { id, file }
  const [completedDoc, setCompletedDoc] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [extractionLoading, setExtractionLoading] = useState(false);

  const handleFiles = (files) => {
    const f = files[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    setTile({ id: 1, file: f });
  };

  const handleComplete = async (doc) => {
    setCompletedDoc(doc);
    setExtractionLoading(true);
    trackEvent('first_value', { path: 'upload', document_id: doc?.id });
    trackEvent('first_upload_completed', { document_id: doc?.id });
    // Pull the latest insight to render the value reveal.
    try {
      const { data } = await axios.get(`${API}/home/latest-insight`);
      if (data?.has_insight) setExtraction(data);
    } catch { /* graceful */ }
    setExtractionLoading(false);
  };

  const askKindra = () => {
    const seed = extraction?.ask_prompt || `What does ${completedDoc?.filename || 'my report'} mean for me?`;
    navigate('/chat', { state: { prefillMessage: seed } });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.4 }}
      data-testid="welcome-upload"
    >
      <PhaseHeader
        onBack={onBack}
        eyebrow="Upload a report"
        title={completedDoc ? "Here's what Kindra found." : "Drop in your most recent report."}
      />

      {!completedDoc && !tile && (
        <>
          <p className="text-[13.5px] text-slate-500 leading-relaxed mb-4 max-w-md">
            Bloods, pathology, imaging, specialist letter, prescription — anything from the last 12 months works best.
          </p>
          <UploadDropzone onFiles={handleFiles} />
          <div className="mt-4 flex items-center gap-1.5 text-[11.5px] text-slate-500">
            <ShieldCheck size={12} weight="duotone" className="text-[#2FB3A0]" />
            <span>Encrypted · yours to delete anytime · stays private.</span>
          </div>
        </>
      )}

      {tile && !completedDoc && (
        <UploadProgressTile
          tileId={tile.id}
          file={tile.file}
          category="other"
          onComplete={handleComplete}
          onCancel={() => setTile(null)}
        />
      )}

      {completedDoc && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="space-y-4"
          data-testid="welcome-upload-findings"
        >
          {/* Findings panel */}
          <div className="bg-white border border-[#ECE8E1] rounded-2xl p-5">
            {extractionLoading && (
              <div className="flex items-center gap-2 text-[13px] text-slate-500">
                <Spinner size={14} className="animate-spin" /> Pulling out the key findings…
              </div>
            )}
            {!extractionLoading && extraction && (
              <>
                {extraction.headline && (
                  <p className="font-serif text-[20px] leading-snug text-[#0E1C2E] mb-3">
                    {extraction.headline}
                  </p>
                )}
                {Array.isArray(extraction.findings) && extraction.findings.length > 0 && (
                  <div className="space-y-2.5" data-testid="welcome-findings-list">
                    {extraction.findings.map((f, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <span className="mt-2 w-1 h-1 rounded-full bg-[#2FB3A0] flex-shrink-0" />
                        <p className="text-[13.5px] text-[#0E1C2E] leading-relaxed">{f}</p>
                      </div>
                    ))}
                  </div>
                )}
                {(!extraction.findings || extraction.findings.length === 0) && (
                  <p className="text-[13px] text-slate-500 leading-relaxed">
                    Saved to your timeline. Open it from your Documents tab anytime — Kindra will pull from it in every chat.
                  </p>
                )}
              </>
            )}
            {!extractionLoading && !extraction && (
              <p className="text-[13px] text-slate-500 leading-relaxed">
                Saved to your timeline. Kindra will use it in every future chat.
              </p>
            )}
          </div>

          {/* Doctor questions hint */}
          <div className="bg-[#FBFAF6] border border-[#ECE8E1] rounded-2xl p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-2">
              What to ask your doctor
            </p>
            <p className="text-[13px] text-slate-600 leading-relaxed">
              Ask Kindra anything about this — what's normal, what changed, what to bring up at your next appointment.
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
            <button
              type="button"
              onClick={askKindra}
              className="inline-flex items-center justify-center gap-2 bg-[#0E1C2E] text-white rounded-xl px-5 py-3 text-[14px] font-semibold hover:bg-[#1A2B42] transition-colors flex-1"
              data-testid="welcome-upload-ask"
            >
              <ChatCircle size={15} weight="fill" />
              Ask Kindra about this
            </button>
            <button
              type="button"
              onClick={onSuccess}
              className="inline-flex items-center justify-center gap-2 border border-[#ECE8E1] bg-white text-[#0E1C2E] rounded-xl px-5 py-3 text-[14px] font-semibold hover:bg-[#FBFAF6] transition-colors"
              data-testid="welcome-upload-done"
            >
              Open my home
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase: CHAT
// ─────────────────────────────────────────────────────────────────────────────
const CHAT_STARTERS = [
  "Why am I tired all the time?",
  "What questions should I bring to my next GP appointment?",
  "Are there things I should ask about my current medications?",
];

const ChatPhase = ({ onBack, onSuccess }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // [{role, content, using_context?}]
  const [sending, setSending] = useState(false);
  const sessionIdRef = useRef(null);
  const valueFiredRef = useRef(false);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: 'user', content: message }]);
    setInput('');

    try {
      const form = new FormData();
      form.append('message', message);
      if (sessionIdRef.current) form.append('session_id', sessionIdRef.current);
      const { data } = await axios.post(`${API}/chat`, form);
      sessionIdRef.current = data.session_id || sessionIdRef.current;
      const reply = data?.message || data?.response || data?.reply || '';
      const usingCtx = data?.using_context || data?.message_meta?.using_context || '';
      setMessages((m) => [...m, { role: 'assistant', content: reply, using_context: usingCtx }]);
      if (!valueFiredRef.current) {
        valueFiredRef.current = true;
        trackEvent('first_value', { path: 'chat' });
        trackEvent('first_chat_completed', { surface: 'welcome' });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Try again?';
      setMessages((m) => [...m, { role: 'assistant', content: detail, error: true }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.4 }}
      data-testid="welcome-chat"
    >
      <PhaseHeader
        onBack={onBack}
        eyebrow="Ask a health question"
        title="What's on your mind?"
      />

      {/* Suggested prompts (only show when no messages yet) */}
      {messages.length === 0 && (
        <div className="space-y-2 mb-5" data-testid="welcome-chat-prompts">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 mb-2">Or try one of these</p>
          {CHAT_STARTERS.map((q, i) => (
            <button
              key={i}
              type="button"
              onClick={() => send(q)}
              disabled={sending}
              className="w-full text-left bg-white border border-[#ECE8E1] rounded-xl px-4 py-3 hover:border-[#CFC8BB] hover:bg-[#FBFAF6] transition-colors text-[13.5px] text-[#0E1C2E] disabled:opacity-50"
              data-testid={`welcome-chat-prompt-${i}`}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      {messages.length > 0 && (
        <div className="space-y-4 mb-5" data-testid="welcome-chat-thread">
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'text-right' : ''}>
              {m.role === 'user' ? (
                <div className="inline-block bg-[#0E1C2E] text-white rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[85%] text-[14px] text-left">
                  {m.content}
                </div>
              ) : (
                <div>
                  <div className="bg-white border border-[#ECE8E1] rounded-2xl rounded-tl-md px-4 py-3 text-[14px] text-[#0E1C2E] leading-relaxed whitespace-pre-wrap">
                    {m.content || (sending && i === messages.length - 1 ? <Spinner size={14} className="animate-spin" /> : '')}
                  </div>
                  {m.using_context && <SourceTrustStrip text={m.using_context} />}
                </div>
              )}
            </div>
          ))}
          {sending && messages[messages.length - 1]?.role === 'user' && (
            <div className="inline-flex items-center gap-2 text-[13px] text-slate-500 bg-white border border-[#ECE8E1] rounded-2xl rounded-tl-md px-4 py-3">
              <Spinner size={14} className="animate-spin" /> Kindra is thinking…
            </div>
          )}
        </div>
      )}

      {/* Composer */}
      <div className="bg-white border border-[#ECE8E1] rounded-xl flex items-center gap-2 px-4 py-2.5">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type your question…"
          className="flex-1 bg-transparent text-[15px] outline-none placeholder:text-slate-400"
          style={{ fontSize: '16px' }}
          disabled={sending}
          data-testid="welcome-chat-input"
        />
        <button
          type="button"
          onClick={() => send()}
          disabled={sending || !input.trim()}
          className="p-2 bg-[#0E1C2E] text-white rounded-lg disabled:opacity-30 hover:bg-[#1A2B42] transition-colors"
          data-testid="welcome-chat-send"
        >
          {sending ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneRight size={14} />}
        </button>
      </div>

      {/* Done button — appears after first reply */}
      {messages.some((m) => m.role === 'assistant' && !m.error) && (
        <div className="mt-5">
          <button
            type="button"
            onClick={onSuccess}
            className="inline-flex items-center justify-center gap-2 border border-[#ECE8E1] bg-white text-[#0E1C2E] rounded-xl px-5 py-3 text-[14px] font-semibold hover:bg-[#FBFAF6] transition-colors w-full sm:w-auto"
            data-testid="welcome-chat-done"
          >
            Open my home
          </button>
        </div>
      )}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase: CONDITION
// ─────────────────────────────────────────────────────────────────────────────
const ConditionPhase = ({ onBack, onSuccess }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addedName, setAddedName] = useState(null);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    let cancel = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/conditions/search`, { params: { q: query } });
        if (!cancel) setResults((data || []).slice(0, 6));
      } catch { if (!cancel) setResults([]); }
      finally { if (!cancel) setSearching(false); }
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [query]);

  const addCondition = async (name, category) => {
    if (adding) return;
    setAdding(true);
    try {
      await axios.post(`${API}/conditions/add`, { name, category });
      setAddedName(name);
      trackEvent('first_value', { path: 'condition', condition: name });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add. Try again?');
    } finally {
      setAdding(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.4 }}
      data-testid="welcome-condition"
    >
      <PhaseHeader
        onBack={onBack}
        eyebrow="Add one condition"
        title={addedName ? "Locked in. Kindra remembers." : "What should Kindra know about?"}
      />

      {!addedName && (
        <>
          <p className="text-[13.5px] text-slate-500 leading-relaxed mb-4 max-w-md">
            Anything you've been told you have, are managing, or watching closely. Type a few letters — Kindra will suggest matches.
          </p>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. asthma, anxiety, hypertension…"
            className="w-full bg-white border border-[#ECE8E1] rounded-xl px-4 py-3 text-[15px] outline-none focus:border-[#CFC8BB] placeholder:text-slate-400"
            style={{ fontSize: '16px' }}
            autoFocus
            data-testid="welcome-condition-input"
          />

          {/* Results */}
          {results.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="welcome-condition-results">
              {results.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  disabled={adding}
                  onClick={() => addCondition(c.name, c.category)}
                  className="w-full text-left bg-white border border-[#ECE8E1] rounded-xl px-4 py-3 hover:border-[#CFC8BB] hover:bg-[#FBFAF6] transition-colors disabled:opacity-50 flex items-center justify-between gap-3"
                  data-testid={`welcome-condition-result-${i}`}
                >
                  <div>
                    <p className="text-[14px] font-medium text-[#0E1C2E]">{c.name}</p>
                    {c.category && <p className="text-[11px] text-slate-500 capitalize mt-0.5">{c.category.replace(/_/g, ' ')}</p>}
                  </div>
                  <ArrowRight size={13} weight="bold" className="text-slate-400" />
                </button>
              ))}
            </div>
          )}

          {/* No-match: free-text add */}
          {query.length >= 2 && !searching && results.length === 0 && (
            <button
              type="button"
              disabled={adding}
              onClick={() => addCondition(query.trim(), 'other')}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#0E1C2E] text-white rounded-xl px-5 py-3 text-[14px] font-semibold disabled:opacity-50 hover:bg-[#1A2B42] transition-colors"
              data-testid="welcome-condition-add-free"
            >
              {adding ? <Spinner size={14} className="animate-spin" /> : null}
              Add "{query.trim()}" as a condition
            </button>
          )}
        </>
      )}

      {addedName && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-4"
          data-testid="welcome-condition-done"
        >
          <div className="bg-white border border-[#ECE8E1] rounded-2xl p-5">
            <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700 mb-2">
              <CheckCircle size={12} weight="fill" /> Added to your record
            </div>
            <p className="font-serif text-[22px] leading-snug text-[#0E1C2E]">
              <em className="italic">{addedName}</em>
            </p>
            <p className="text-[13px] text-slate-500 leading-relaxed mt-2">
              Every chat from here on factors this in. Kindra will also flag medications and lab values that interact with it when you upload reports.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              type="button"
              onClick={() => { setQuery(''); setAddedName(null); setResults([]); }}
              className="inline-flex items-center justify-center gap-2 border border-[#ECE8E1] bg-white text-[#0E1C2E] rounded-xl px-5 py-3 text-[14px] font-semibold hover:bg-[#FBFAF6] transition-colors flex-1"
              data-testid="welcome-condition-add-another"
            >
              Add another
            </button>
            <button
              type="button"
              onClick={onSuccess}
              className="inline-flex items-center justify-center gap-2 bg-[#0E1C2E] text-white rounded-xl px-5 py-3 text-[14px] font-semibold hover:bg-[#1A2B42] transition-colors flex-1"
              data-testid="welcome-condition-done-cta"
            >
              I'm done — open my home
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase: DEMO (3-slide editorial walkthrough — no upload required)
// ─────────────────────────────────────────────────────────────────────────────
const DEMO_SLIDES = [
  {
    eyebrow: 'Your reports, in plain English',
    title: '"Your iron is low — here\'s what that probably feels like."',
    body: 'Kindra reads your blood tests, scans, and letters and explains them like a careful, patient friend who happens to know medicine.',
    chips: ['ferritin 14 µg/L', 'haemoglobin normal', 'follow-up in 6 weeks'],
  },
  {
    eyebrow: 'Personal context, not generic answers',
    title: '"Given your hypothyroidism and the iron tablet, here\'s what to ask…"',
    body: 'Every chat factors in your conditions, medications, and recent results — so the answer is yours, not a textbook.',
    chips: ['Hashimoto\'s', 'Levothyroxine 50mcg', 'iron polymaltose'],
  },
  {
    eyebrow: 'Walk into appointments prepared',
    title: '"Three questions to bring to your endocrinology review."',
    body: 'Kindra remembers your history and drafts the exact questions worth asking — so the 10 minutes with your doctor count.',
    chips: ['endocrinology', 'TSH trend', 'medication review'],
  },
];

const DemoPhase = ({ onBack, onSuccess }) => {
  const [slide, setSlide] = useState(0);
  const valueFiredRef = useRef(false);

  useEffect(() => {
    if (slide >= 1 && !valueFiredRef.current) {
      valueFiredRef.current = true;
      trackEvent('first_value', { path: 'demo' });
    }
  }, [slide]);

  const total = DEMO_SLIDES.length;
  const isLast = slide === total - 1;
  const s = DEMO_SLIDES[slide];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.4 }}
      data-testid="welcome-demo"
    >
      <PhaseHeader
        onBack={onBack}
        eyebrow="See what Kindra can do"
        title="A 60-second tour."
      />

      {/* Slide */}
      <motion.div
        key={slide}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="bg-white border border-[#ECE8E1] rounded-2xl p-6 sm:p-7"
        data-testid={`welcome-demo-slide-${slide}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <BookBookmark size={11} weight="duotone" className="text-[#2FB3A0]" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            {s.eyebrow}
          </span>
        </div>
        <p className="font-serif text-[22px] sm:text-[24px] leading-snug text-[#0E1C2E]">
          {s.title}
        </p>
        <p className="text-[13.5px] text-slate-600 leading-relaxed mt-3">
          {s.body}
        </p>
        {s.chips?.length > 0 && (
          <div className="mt-4 flex items-center gap-1.5 flex-wrap">
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">Kindra used</span>
            {s.chips.map((c, i) => (
              <span key={i} className="inline-flex rounded-md bg-[#2FB3A0]/[0.08] border border-[#2FB3A0]/20 px-2 py-0.5 text-[11px] text-[#0E1C2E] leading-tight">
                {c}
              </span>
            ))}
          </div>
        )}
      </motion.div>

      {/* Pager + actions */}
      <div className="mt-5 flex items-center justify-between">
        <div className="flex items-center gap-1.5" data-testid="welcome-demo-pager">
          {DEMO_SLIDES.map((_, i) => (
            <span
              key={i}
              className={`block w-1.5 h-1.5 rounded-full transition-colors ${i === slide ? 'bg-[#0E1C2E]' : 'bg-slate-300'}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          {!isLast && (
            <button
              type="button"
              onClick={() => setSlide((n) => n + 1)}
              className="inline-flex items-center gap-1.5 bg-[#0E1C2E] text-white rounded-xl px-4 py-2.5 text-[13px] font-semibold hover:bg-[#1A2B42] transition-colors"
              data-testid="welcome-demo-next"
            >
              Next <ArrowRight size={12} weight="bold" />
            </button>
          )}
          {isLast && (
            <button
              type="button"
              onClick={onSuccess}
              className="inline-flex items-center gap-1.5 bg-[#0E1C2E] text-white rounded-xl px-4 py-2.5 text-[13px] font-semibold hover:bg-[#1A2B42] transition-colors"
              data-testid="welcome-demo-done"
            >
              Open my Kindra
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default WelcomePage;
